import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  useIncidentTypes,
  useCreateIncident,
  useUpdateIncident,
  getSeverityLabel,
} from '@/hooks/useRPL';
import { incidentFormSchema, type IncidentFormData } from '@/schemas/rplSchemas';
import type { Incident, IncidentSeverity } from '@/types/rpl';
import { api } from '@/lib/apiClient';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

interface IncidentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incident?: Incident | null;
}

const SEVERITIES: IncidentSeverity[] = ['minor', 'moderate', 'serious', 'critical'];

export function IncidentFormDialog({ open, onOpenChange, incident }: IncidentFormDialogProps) {
  const { data: incidentTypes } = useIncidentTypes(true);
  const createMutation = useCreateIncident();
  const updateMutation = useUpdateIncident();
  const [loading, setLoading] = useState(false);

  // Fetch employees for victim selection
  const { data: employees } = useQuery({
    queryKey: ['profiles-active'],
    queryFn: async () => {
      const { data, error } = await api
        .from('profiles')
        .select('id, full_name')
        .eq('is_active', true)
        .order('full_name');
      if (error) throw error;
      return data as { id: string; full_name: string }[];
    },
  });

  // Fetch machines
  const { data: machines } = useQuery({
    queryKey: ['machines-active'],
    queryFn: async () => {
      const { data, error } = await api
        .from('machines')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const form = useForm<IncidentFormData>({
    resolver: zodResolver(incidentFormSchema),
    defaultValues: {
      incident_type_id: '',
      title: '',
      description: '',
      severity: 'minor',
      category: '',
      occurred_at: new Date().toISOString().slice(0, 16),
      location: '',
      area: '',
      victim_id: '',
      immediate_actions: '',
      injury_type: '',
      days_lost: 0,
      medical_attention: false,
      hospitalization: false,
      work_order_id: '',
      machine_id: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        incident_type_id: incident?.incident_type_id || '',
        title: incident?.title || '',
        description: incident?.description || '',
        severity: incident?.severity || 'minor',
        category: incident?.category || '',
        occurred_at: incident?.occurred_at
          ? new Date(incident.occurred_at).toISOString().slice(0, 16)
          : new Date().toISOString().slice(0, 16),
        location: incident?.location || '',
        area: incident?.area || '',
        victim_id: incident?.victim_id || '',
        immediate_actions: incident?.immediate_actions || '',
        injury_type: incident?.injury_type || '',
        days_lost: incident?.days_lost || 0,
        medical_attention: incident?.medical_attention || false,
        hospitalization: incident?.hospitalization || false,
        work_order_id: incident?.work_order_id || '',
        machine_id: incident?.machine_id || '',
        notes: incident?.notes || '',
      });
    }
  }, [open, incident, form]);

  // Auto-set severity when incident type changes
  const watchIncidentType = form.watch('incident_type_id');
  useEffect(() => {
    if (watchIncidentType && !incident) {
      const selectedType = incidentTypes?.find((t) => t.id === watchIncidentType);
      if (selectedType) {
        form.setValue('severity', selectedType.severity_default);
      }
    }
  }, [watchIncidentType, incidentTypes, incident, form]);

  const onSubmit = async (data: IncidentFormData) => {
    setLoading(true);
    try {
      const payload = {
        incident_type_id: data.incident_type_id || null,
        title: data.title,
        description: data.description,
        severity: data.severity,
        category: data.category || null,
        occurred_at: data.occurred_at,
        location: data.location || null,
        area: data.area || null,
        victim_id: data.victim_id || null,
        immediate_actions: data.immediate_actions || null,
        injury_type: data.injury_type || null,
        days_lost: data.days_lost,
        medical_attention: data.medical_attention,
        hospitalization: data.hospitalization,
        work_order_id: data.work_order_id || null,
        machine_id: data.machine_id || null,
        notes: data.notes || null,
      };

      if (incident) {
        await updateMutation.mutateAsync({ id: incident.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{incident ? 'Editar Incidente' : 'Reportar Incidente'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Titulo *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Breve descripcion del incidente" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="incident_type_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de incidente</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {incidentTypes?.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gravedad *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SEVERITIES.map((sev) => (
                          <SelectItem key={sev} value={sev}>
                            {getSeverityLabel(sev)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="occurred_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha y hora *</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ubicacion</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Nave 1, Zona de corte" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripcion detallada *</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="Describe que ocurrio..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="victim_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Persona afectada</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Ninguna</SelectItem>
                        {employees?.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="machine_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maquina involucrada</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Ninguna</SelectItem>
                        {machines?.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="injury_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de lesion</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Corte superficial" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="days_lost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dias de baja</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="immediate_actions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Acciones inmediatas tomadas</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="text-sm">Requirio atencion medica</span>
                <FormField
                  control={form.control}
                  name="medical_attention"
                  render={({ field }) => (
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  )}
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="text-sm">Hospitalizacion</span>
                <FormField
                  control={form.control}
                  name="hospitalization"
                  render={({ field }) => (
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {incident ? 'Actualizar' : 'Reportar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
